import {
  Box,
  Button,
  Flex,
  Heading,
  Stack,
  Text,
  useMediaQuery,
} from "@chakra-ui/react";
import React, { useEffect, useRef, useState } from "react";
import { Download } from "react-feather";
import { useReactToPrint } from "react-to-print";
import BalanceSheetReport from "../../reports/balanceSheetReport";
import { PdfContent } from "../../reports/performanceReviewReport";
import ProfitLossReport from "../../reports/profitLossReport";
import PeriodSelect from "../../reports/reportComponents/periodSelect";
import DashboardDropdown from "../dashboardDateComponent";
import RoyaltyReport from "../../reports/royaltyReport";
import YtdToggle from "../../dashboardComponents/ytdToggle";
import TargetTypeToggle from "../../dashboardComponents/targetToggle";

const formatDate = (date) => {
  return date.toLocaleDateString("en-US", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
};

export const ReportPage = ({
  customer,
  type = "",
  title = "",
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  selectedCategory,
  setSelectedCategory,
  performanceEndDate,
  setPerformanceEndDate,
  defaultDate,
  performaceCategory,
  setPerformanceCategory,
}) => {
  const [isMobile] = useMediaQuery("(max-width: 1280px)", { ssr: false });
  const [mobileCollapse] = useMediaQuery("(max-width: 900px)", { ssr: false });
  const [mobileDeepCollapse] = useMediaQuery("(max-width: 593px)", {
    ssr: false,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!printRef.current) return;

    // Set loading to true immediately when dependencies change
    setLoading(true);

    let timeoutId = null;

    const observerMutation = new MutationObserver(() => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setLoading(false); // Done rendering
      }, 1000);
    });

    const observerResize = new ResizeObserver(() => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setLoading(false); // Layout settled
      }, 1000);
    });

    observerMutation.observe(printRef.current, {
      childList: true,
      subtree: true,
      attributes: true,
    });

    observerResize.observe(printRef.current);

    return () => {
      observerMutation.disconnect();
      observerResize.disconnect();
      clearTimeout(timeoutId);
    };
  }, [customer, endDate, startDate, performanceEndDate]);

  const reportShortDate = endDate
    .toLocaleString("en-US", {
      month: "short",
      year: "numeric",
    })
    .toUpperCase();

  const printRef = useRef(null);

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    pageStyle:
      type === "performance"
        ? `
        @page {
          size: A4 portrait;
          margin: 0;
        }
        @media print {
          body {
            -webkit-print-color-adjust: exact;
          }
          canvas {
            image-rendering: crisp-edges;
            width: 6in; /* Set physical size */
            height: 4in;
            display: block;
          }
          .no-print {
            display: none !important;
          }
        }
      `
        : type === "profitLoss"
        ? `
      @page {
        size: A4 portrait;
        margin: 20px 60px;
      }
      @media print {
        body {
          -webkit-print-color-adjust: exact;
        }

      }
    `
        : `
      @page {
        size: A4 portrait;
        margin: 20px 60px;
      }
      @media print {
        body {
          -webkit-print-color-adjust: exact;
        }
      }
    `,
    documentTitle:
      type === "performance"
        ? `Performance Review ${reportShortDate}`
        : type === "profitLoss"
        ? `Profit & Loss Report ${formatDate(startDate)}-${formatDate(endDate)}`
        : type === "royalty"
        ? `Franchise Statement ${formatDate(startDate)}-${formatDate(endDate)}`
        : `Balance Sheet Report ${formatDate(endDate)}`,
  });

  return (
    <Box mt={isMobile ? "142px" : "92px"} mx={"30px"}>
      <Box mb={"40px"}>
        <Flex
          direction="row"
          wrap="wrap"
          align="center"
          justify="space-between"
          gap={4}
          mb={mobileCollapse ? "10px" : "0px"}
        >
          <Heading fontSize="24px">{title}</Heading>

          <Flex direction="row" wrap="wrap" align="center" gap={3}>
            {type === "performance" ? (
              <DashboardDropdown
                key={performanceEndDate?.toISOString()}
                height="36px"
                dateFromNav={defaultDate}
                setEnd={setPerformanceEndDate}
                endDate={performanceEndDate}
                category={performaceCategory}
                setCategory={setPerformanceCategory}
                direction={mobileDeepCollapse ? "left" : "right"}
                includeAllFromMin={true}
              />
            ) : (
              <PeriodSelect
                check2={startDate?.toISOString()}
                check1={endDate?.toISOString()}
                dateFromNav={defaultDate}
                startDate={startDate}
                endDate={endDate}
                setEnd={setEndDate}
                setStart={setStartDate}
                category={selectedCategory}
                setCategory={setSelectedCategory}
                direction={mobileDeepCollapse ? "left" : "right"}
              />
            )}
            {(type === "performance" && customer?.is_demo ===
                    false) &&
                     (

              <Box>
                <TargetTypeToggle></TargetTypeToggle>
              </Box>
            )}
            <Button
              onClick={handlePrint}
              whiteSpace="nowrap"
              isDisabled={loading}
            >
              {!loading ? (
                <>
                  <Download size="15px" />
                  <Text pl="5px">Download</Text>
                </>
              ) : (
                <Text pl="5px">...Preparing</Text>
              )}
            </Button>
          </Flex>
        </Flex>
        {type === "performance" && (
          <Stack p="20px 0px 20px 0px">
            <YtdToggle></YtdToggle>
          </Stack>
        )}
      </Box>
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100%"
        mb={"30px"}
      >
        {type === "performance" && (
          <PdfContent
            customer={customer}
            externalEndDate={performanceEndDate}
            hideButtons={true}
            externalPrintRef={printRef}
          />
        )}
        {type === "profitLoss" && (
          <ProfitLossReport
            customer={customer}
            externalStartDate={startDate}
            externalEndDate={endDate}
            hideButtons={true}
            externalPrintRef={printRef}
          />
        )}
        {type === "balanceSheet" && (
          <BalanceSheetReport
            customer={customer}
            externalEndDate={endDate}
            externalStartDate={startDate}
            hideButtons={true}
            externalPrintRef={printRef}
          />
        )}
        {type === "royalty" && (
          <RoyaltyReport
            customer={customer}
            externalStartDate={startDate}
            externalEndDate={endDate}
            hideButtons={true}
            externalPrintRef={printRef}
          />
        )}
      </Box>
    </Box>
  );
};
